import { useState, useRef, useEffect, useCallback } from 'react';
import { units } from '@ui/css';
import { throttleToAnimationFrame, asyncTimeout, isPlatformSafari } from '@utils/common';
import { combineTokens, applyCssDeclarations } from '@utils/css';

/**
 * The total amount of lines the component is allowed to render when it can
 * expand and is collapsed.
 */
const COLLAPSED_LINES = 8;

/**
 * Specifies the duration in ms it takes to transition from collapsed to
 * expanded and vice versa
 */
const TRANSITION_DURATION_MS = 625;
const expandCollapseTokens = combineTokens("--text--expand-collapse", {
  lines: COLLAPSED_LINES,
  height: 0
});
function useExpandCollapse(collapsible, {
  wrapperRef,
  ref
}, options, callbacks) {
  const [canExpand, setCanExpand] = useState(collapsible);
  const [height, setHeight] = useState(determineCollapsedHeight(ref));
  const transitioningRef = useRef(false);
  const [{
    isExpanded,
    isTransitioning
  }, setState] = useState({
    isTransitioning: false,
    isExpanded: options.initiallyExpanded
  });
  useEffect(() => {
    applyCssDeclarations(expandCollapseTokens.__override({
      lines: (isTransitioning || isExpanded || !canExpand) && !options.scrambled ?
      // using "initial" will cause different collapsing of margins compared to
      // when line-clamp has been set to a specific value, hence we use
      // a very large number to ensure the same margin collapsing at all
      // times while still mimicking the "initial" property
      "10000000000000000" : `${COLLAPSED_LINES}`,
      height: units.px(height)
    }), ref);
  }, [canExpand, isExpanded, isTransitioning, height, options.scrambled, ref]);
  useEffect(() => {
    function canComponentExpand() {
      const content = ref.current;

      // If the model doesn't specify that the component is collapsible, then
      // it should never expand/collapse.
      if (!collapsible || !content) {
        setCanExpand(false);
        return;
      }
      const expandedHeight = content.scrollHeight;
      if (expandedHeight == 0) {
        // In case the component is being opened with an accordion its height
        // may be 0 when initially rendering, making it impossible for us to
        // determine if it can expand or not.. Try again in a little bit.
        setTimeout(() => {
          canComponentExpand();
        }, 20);
        return;
      }

      // The component should only be able to expand if its content is larger
      // than the collapsed height of the component
      const collapsedHeight = determineCollapsedHeight(ref);
      setHeight(collapsedHeight);
      setCanExpand(expandedHeight > collapsedHeight);
    }
    const throttled = throttleToAnimationFrame(canComponentExpand);
    canComponentExpand();
    const wrapper = wrapperRef.current;
    let resizeObserver;
    if (typeof ResizeObserver !== "undefined" && !!wrapper) {
      resizeObserver = new ResizeObserver(() => {
        // Do not bother checking whether or not the component can expand if the
        // resize was triggered due to the collpase/expand transition..
        if (!transitioningRef.current) {
          throttled();
        }
      });
      resizeObserver.observe(wrapper);
    }
    return () => {
      resizeObserver?.disconnect();
    };
  }, [ref, wrapperRef, collapsible]);
  const expand = useCallback(async () => {
    const elm = ref.current;
    const wrapper = wrapperRef.current;
    const componentWrapper = wrapper?.parentElement;
    if (!elm || !componentWrapper || isTransitioning) {
      return;
    }

    // Force current height
    transitioningRef.current = true;
    elm.style.height = `${determineCollapsedHeight(ref)}px`;
    setState(prev => ({
      ...prev,
      isTransitioning: true
    }));

    // Wait 50ms to guarantee that isTransitioning has properly updated, since
    // it affects the webkit-line-clamp which will ensure that the height
    // remains as desired
    await asyncTimeout(50);
    componentWrapper.style.overflowAnchor = "auto";

    // Trigger transition
    elm.style.height = `${elm.scrollHeight}px`;
    await asyncTimeout(TRANSITION_DURATION_MS);
    setState({
      isExpanded: true,
      isTransitioning: false
    });
    // ... same reason as previous timeout of 50ms
    await asyncTimeout(50);
    elm.style.height = "";
    transitioningRef.current = false;
    callbacks.onExpanded?.();
  }, [ref, wrapperRef, isTransitioning, callbacks]);
  const collapse = useCallback(async () => {
    const elm = ref.current;
    const wrapper = wrapperRef.current;
    const componentWrapper = wrapper?.parentElement;
    if (!elm || !componentWrapper || isTransitioning) {
      return;
    }
    transitioningRef.current = true;
    const collapsedHeight = determineCollapsedHeight(ref);

    // Force current height
    elm.style.height = `${elm.scrollHeight}px`;
    setState(prev => ({
      ...prev,
      isTransitioning: true
    }));

    // Wait 50ms to guarantee that isTransitioning has properly updated, since
    // it affacts the webkit-line-clamp which will ensure that the height
    // remains as desired
    await asyncTimeout(50);

    // In order to avoid content jumps when collapsing the container we disable
    // the overflow anchor. This must be done on the component wrapper in order
    // to work as desired.
    componentWrapper.style.overflowAnchor = "none";

    // Trigger transition
    elm.style.height = `${collapsedHeight}px`;
    await asyncTimeout(TRANSITION_DURATION_MS);
    setState({
      isExpanded: false,
      isTransitioning: false
    });

    // ... same reason as previous timeout of 50ms
    await asyncTimeout(50);
    elm.style.height = "";
    transitioningRef.current = false;
    callbacks.onCollapsed?.();
  }, [ref, wrapperRef, isTransitioning, callbacks]);
  return {
    canExpand,
    isExpanded,
    isTransitioning,
    expand,
    collapse
  };
}
function determineCollapsedHeight(ref) {
  const content = ref.current;
  if (!content) {
    return 0;
  }

  // First of all, force cropping after the desired number of lines!
  content.style.setProperty("line-clamp", `${COLLAPSED_LINES}`);
  content.style.setProperty("-webkit-line-clamp", `${COLLAPSED_LINES}`);
  content.style.setProperty("-moz-line-clamp", `${COLLAPSED_LINES}`);
  content.style.setProperty("min-height", "unset");

  // Calculate the height now that the number of lines have been cropped!
  let collapsedHeight = content.clientHeight;

  // Safari fails to recognize line-clamping on elements that have block
  // children inside. In this case .clientHeight will compute to 0 when
  // line-height is set to a low number and .scrollHeight when line-clamping is
  // set to a VERY large number.
  //
  // That is not what we want.. This method should return the actual height of
  // the content element when it is cropped to the collapsed amount of lines,
  // and thus if any of the two conditions is true, then we must manually
  // extract the required height to fit exactly the desired amount of lines.
  if (collapsedHeight === 0 || isPlatformSafari() || content.clientHeight === content.scrollHeight) {
    collapsedHeight = getHeightRequiredToFitLines(content, COLLAPSED_LINES);
  }

  // Finally re-allow the content to contain whatever amount of text it is
  // supposed to
  content.style.setProperty("line-clamp", "");
  content.style.setProperty("-webkit-line-clamp", "");
  content.style.setProperty("-moz-line-clamp", "");
  content.style.setProperty("min-height", "");
  return collapsedHeight;
}

/**
 * Recursively computes the total height required such the provided parent
 * contains exactly the specified lineCount.
 *
 * NB: Simply using lineHeight / lineCount is not sufficient as this method
 * supports block children that may contain margin, different font-sizes /
 * line-heights and more. As such a recursive implementation has been
 * implemented to achieve this.
 */
function getHeightRequiredToFitLines(parent, lineCount) {
  let height = 0;
  let currentLineCount = 0;
  let prevSiblingName = "";
  let prevChildHeight = 0;
  let prevOffsetTop = -1;
  let prevMarginBottom = 0;
  for (const child of Array.from(parent.children)) {
    const childStyles = getComputedStyle(child);
    const childHeight = child.scrollHeight || child.getBoundingClientRect().height;
    const childMarginBottom = parseFloat(childStyles["marginBottom"]);
    const lineHeight = parseFloat(childStyles.lineHeight);
    let linesInChild = Math.round(childHeight / lineHeight);

    // Line-breaks add slight spacing between lines, make sure we compensate for
    // this
    if (child.tagName === "BR") {
      // In case of multiple line-breaks then all but the first adds a new line
      if (prevSiblingName === "BR") {
        currentLineCount += 1;
        height += lineHeight;
      } else {
        height += 3;
      }
      prevSiblingName = child.tagName;
      continue;
    }

    // We only need to consider children that are blocks as they are the only
    // which will actually contain lines (whereas inline elements consitute to
    // the blocks total lines)
    if (!(child instanceof HTMLElement)) {
      continue;
    }

    // In case the current child pushes us over the desired amount of lines,
    // then focus analysis on this element! ...
    if (linesInChild + currentLineCount >= lineCount) {
      // In case the child which cases overflow contains children, then analyse
      // these to determine if block children exists inside...
      if (child.children.length > 0) {
        height += getHeightRequiredToFitLines(child, lineCount - currentLineCount);
      } else {
        // ... else we can simply add the total amount of lines that this
        // element has.
        height += (lineCount - currentLineCount) * lineHeight;
      }
      break;
    }

    // In case we've encountered two inline children in a row that have been on
    // the same line, then simply negate the line that the previous child added
    // (since it'll be re-applied by this child)
    if (prevOffsetTop === child.offsetTop) {
      linesInChild -= 1;
      height -= prevChildHeight;
    }

    // ... otherwise, in case the current child does not constitue an overflow,
    // then simply add its height to the total height required to obtain the
    // desired lineCount
    currentLineCount += linesInChild;
    height += childHeight + Math.max(0, parseFloat(childStyles["marginTop"]) - prevMarginBottom) + childMarginBottom;
    prevOffsetTop = child.offsetTop;
    prevMarginBottom = childMarginBottom;
    prevSiblingName = child.tagName;
    prevChildHeight = childHeight;
  }

  // In case the provided parent didn't have any relevant children, then we can
  // fallback to the regular lineCount / lineHeight trick to determine the
  // required height
  if (height === 0) {
    const style = getComputedStyle(parent);
    height += lineCount * parseFloat(style.lineHeight);
  }
  return height;
}

export { COLLAPSED_LINES, TRANSITION_DURATION_MS, expandCollapseTokens, getHeightRequiredToFitLines, useExpandCollapse };
//# sourceMappingURL=useExpandCollapse.js.map
